<template>
  <div
    :class="{
      [containerSize]: true,
      'central-design': useCenterVerticalDesktopDesign || (isHideAvatar && !isKeepAvatarDesign),
      'bottom-navigation-enabled': currentConfig.isBottomNavigationEnabled,
    }"
    class="website"
  >
    <close-website-button
      v-if="isNotFullsizeWebsite"
      class="close"
    />
    <iframe
      :src="websiteUrl"
      class="frame"
      allow="camera; microphone; autoplay; display-capture;"
    >
      Your browser does not support frames
    </iframe>
  </div>
</template>

<script>
import {
  computed, defineComponent, onMounted, onUnmounted, ref, watch,
} from 'vue';
import jsonpath from 'jsonpath';
import useActions from '@/composables/useActions';
import useDynamicUrl from '@/composables/useDynamicUrl';
import useConfig from '@/composables/useConfig';
import useContent from '@/composables/useContent';
import useWebsite from '@/composables/useWebsite';
import useWebsitePlugin from '@/composables/useWebsitePlugin';
import CloseWebsiteButton from '@/components/CloseWebsiteButton.vue';
import { FRAME_PROTOCOL, VARIABLE_VALUE_TYPES } from '@/js/constants';
import actionTypes from '@/actions/types';

export default defineComponent({
  name: 'WebsiteComponent',

  components: { CloseWebsiteButton },

  setup() {
    const { currentContent, isPaused, useCenterVerticalDesktopDesign, isHideAvatar, isKeepAvatarDesign } = useContent();
    const { createAction } = useActions();
    const { setVariables, currentConfig } = useConfig();
    const { handleClose } = useWebsitePlugin();
    const { isNotFullsizeWebsite } = useWebsite();
    const params = computed(() => currentContent.value?.attrs?.params || []);
    const websiteUrl = computed(() => {
      const url = new URL(useDynamicUrl(currentContent.value?.attrs?.website));
      params.value.forEach(({ parameter, value }) => {
        url.searchParams.append(useDynamicUrl(parameter), decodeURIComponent(useDynamicUrl(value)));
      });
      return url.toString();
    });

    const containerSize = computed(() => currentContent.value?.attrs?.containerSize);
    const sayAction = ref(null);

    const handleMessage = (message) => {
      switch (message.data?.action) {
        case FRAME_PROTOCOL.SPEECH: {
          if (sayAction.value) {
            sayAction.value.stop();
          }
          sayAction.value = createAction(actionTypes.SAY, { utterance: message.data.payload?.utterance }, 'website');
          sayAction.value?.execute();

          break;
        }
        case FRAME_PROTOCOL.SET_VARIABLES: {
          const { variables } = currentContent.value.attrs;
          if (variables) {
            const variablesToSet = variables.reduce((acc, { id, path }) => {
              const value = jsonpath.value(message.data.payload, path);
              if (value !== undefined) {
                acc.push({ id, value: { value, type: VARIABLE_VALUE_TYPES.PRIMITIVE } });
              }

              return acc;
            }, []);
            setVariables(variablesToSet);
          }
          break;
        }
        case FRAME_PROTOCOL.CLOSE: {
          if (message.data.payload?.stopSpeech) {
            sayAction.value?.stop();
          }
          handleClose();
          break;
        }
        default:
          // eslint-disable-next-line no-console
          console.log('unsupported action', message.data?.action);
      }
    };

    onMounted(() => window.addEventListener('message', handleMessage));
    onUnmounted(() => {
      window.removeEventListener('message', handleMessage);
      sayAction.value?.stop();
    });

    watch(isPaused, () => {
      if (isPaused.value) {
        sayAction.value?.pause();
      } else {
        sayAction.value?.resume();
      }
    });

    return {
      websiteUrl,
      containerSize,
      isNotFullsizeWebsite,
      useCenterVerticalDesktopDesign,
      currentConfig,
      isHideAvatar,
      isKeepAvatarDesign,
    };
  },
});
</script>

<style lang="scss" scoped>
.website {
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background-color: #fff;

  &.bottom-navigation-enabled {
    height: calc(100vh - 64px);
  }

  &.quarter-size, &.halfsize, &.square-size {
    position: absolute;
    width: 90%;
    top: auto;
    right: 0;
    bottom: 24px;
    margin: auto;
    border-radius: 16px;
    overflow: hidden;

    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      top: 0;
      bottom: 0;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      top: 0;
      bottom: 0;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      top: auto;
      bottom: 24px;
    }

    &.central-design {
      @media screen and (orientation: portrait) and (min-width: $desktop_s_breakpoint - 1px) {
        top: 0;
        bottom: 0;
      }
    }
  }

  &.halfsize {
    height: 55%;

    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      height: 90%;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      height: 90%;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      height: 55%;
    }
  }

  &.quarter-size {
    height: 35%;
  }

  &.square-size {
    width: 360px;
    height: 360px;
    max-width: 90%;
    max-height: 90%;

    $sizes:
      $tablet_breakpoint 400px,
      $desktop_s_breakpoint 400px,
      $desktop_m_breakpoint 600px,
      $desktop_l_breakpoint 700px;

    @each $screen-size, $size in $sizes {
      @media screen and (min-width: $screen-size) {
        width: $size;
        height: $size;
      }
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      width: 250px;
      height: 250px;
    }

    @media screen and (min-height: $desktop_m_breakpoint - 1px) and (orientation: portrait) {
      width: 800px;
      height: 800px;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      width: 1500px;
      height: 1500px;
    }
  }
}

.close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
}

.frame {
  border: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
}
</style>
